/*
  Who is on the project, and the only way to change it from the app.

  Three routes have existed since P3 — list, grant-by-email, withdraw — with an authorisation matrix over
  every one of them, and until now `apps/web/src/api.ts` contained no reference to the word `members` at
  all. So an owner could import a project and had no way whatsoever to let a colleague in: the platform's
  entire reason for existing over the frozen single-file app is that more than one person can be in it,
  and the act of adding the second person was reachable only with `curl`.

  NOT A COMMAND, and so not in the change log. Membership is not part of the design — it does not export,
  it is not per scenario, and reverting it makes no sense as a step in the undo chain. It is state about
  the project rather than state of it, which is why it lives on its own routes and why this panel refetches
  rather than listening on the socket.

  READABLE BY EVERY MEMBER, editable only by an owner. A reviewer already sees these names on the presence
  strip and in every change-log row; hiding the list from them would protect nothing and would leave them
  unable to answer "who do I ask about this". The server enforces the write half regardless of what this
  renders — the controls below are hidden from a non-owner as a courtesy, not as the check.
*/
import { useEffect, useState } from 'react';
import type { ProjectMember, ProjectRole } from '@integrate/contracts';
import type { Person } from '@integrate/core';
import { api, ApiError } from '../api.js';
import { Modal } from '../ui/Modal.js';
import { Loading } from '../ui/States.js';
import { ScopeEditor } from './ScopeEditor.js';

/**
 * What each role can do, in the words a person choosing one needs.
 *
 * The vocabulary is `packages/contracts/src/domain.ts`; the sentences are here because they are about the
 * choice rather than about the type. `reviewer` is the one that needs explaining: it is not "editor but
 * less" — it is a distinct route, where the work is submitted as a proposal an editor applies.
 */
const ROLES: { value: ProjectRole; label: string; help: string }[] = [
  { value: 'owner', label: 'Owner', help: 'Everything, including members, restore and deleting the project.' },
  { value: 'editor', label: 'Editor', help: 'Make and undo decisions, within their access. Cannot manage members or delete.' },
  /*
    "EVERYTHING" NO LONGER MEANS EVERYTHING, and the copy says so. A role decides what somebody may
    DO; access decides what they may SEE, and the two are set separately on the row. Leaving these
    sentences as they were would have made the dialog contradict itself the first time a scope was
    granted.
  */
  { value: 'reviewer', label: 'Reviewer', help: 'Read what they have access to, and propose changes for an editor to apply.' },
  { value: 'viewer', label: 'Viewer', help: 'Read and export what they have access to. Changes nothing.' },
];

function roleLabel(role: ProjectRole): string {
  return ROLES.find((r) => r.value === role)?.label ?? role;
}

export function MembersEditor({
  projectId,
  isOwner,
  people,
  currentUserId,
  onLeft,
  onClose,
}: {
  projectId: string;
  isOwner: boolean;
  /**
   * The project's roster, for the scope editor's value lists and its live matched count.
   *
   * Safe to hold here because only an OWNER opens the editor and an owner is never scoped — so this
   * is the whole roster by construction, and the count it produces is the same one the server will
   * arrive at from the same predicate.
   */
  people: readonly Person[];
  /** To mark the row that is you, which is the row whose removal needs thinking about. */
  currentUserId: string;
  /**
   * You have just given up your own access, so there is no project left to be looking at.
   *
   * Staying would leave a screen whose every fetch now answers the deliberately uninformative
   * "Project not found" — the same aftermath the delete route already handles, arrived at from the
   * other direction.
   */
  onLeft(): void;
  onClose(): void;
}): JSX.Element {
  const [members, setMembers] = useState<ProjectMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<ProjectRole>('editor');
  /** Whose access is being edited, or null. One at a time: it is a decision, not a bulk edit. */
  const [editing, setEditing] = useState<string | null>(null);

  const reload = async (): Promise<void> => {
    try {
      const { members: rows } = await api.members(projectId);
      setMembers(rows);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the members.');
    }
  };

  /*
    Once, on the project. Nothing here streams and nothing should: membership is not a command, so it
    emits no event, and a socket frame for it would be a second transport for one rare act. Every write
    below refetches, which is what keeps the list honest after a change another owner made.
  */
  useEffect(() => {
    void reload();
  }, [projectId]);

  /**
   * Every write goes through here, so the refusal has one place to land.
   *
   * The server's message is shown verbatim rather than translated. It is the one that knows WHY — "No
   * user with that email", "A project must keep an owner" — and a client-side paraphrase of a rule the
   * client does not enforce is how the two drift apart.
   */
  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  const grant = (): void => {
    const address = email.trim();
    if (!address) return;
    void run(async () => {
      await api.addMember(projectId, address, role);
      setEmail('');
    });
  };

  return (
    <Modal testId="members-modal" title="People on this project" onClose={onClose}>
          <p className="muted" style={{ marginTop: 0 }}>
            Access is per project. It is not part of the design, so it does not appear in the change log,
            it is not exported with the workbook, and it is the same for every scenario.
          </p>

          {error && (
            <p className="bad" data-testid="members-error" role="alert">
              {error}
            </p>
          )}

          {members === null ? (
            <Loading what="who is on this project" testId="members-loading" />
          ) : (
            <table className="tsa-table" data-testid="members-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Access</th>
                  {/*
                    Always present, because there is now something for a non-owner in it too: leaving
                    is not managing members, and until this existed the only way off a project you had
                    finished with was to ask an owner.
                  */}
                  <th />
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.userId} data-testid={`member-row-${m.userId}`}>
                    <td>
                      {m.name}
                      {m.userId === currentUserId && <span className="muted"> · you</span>}
                    </td>
                    <td className="muted">{m.email}</td>
                    <td>
                      {isOwner ? (
                        /*
                          A select rather than an edit-then-save: the grant route upserts, so choosing a
                          role IS the change, and there is no draft state worth holding. Its own value is
                          read straight off the row, so a change another owner makes lands on a refetch
                          rather than being shadowed by a local copy.
                        */
                        <select
                          data-testid={`member-role-${m.userId}`}
                          value={m.role}
                          disabled={busy}
                          onChange={(e) =>
                            void run(() =>
                              api.addMember(projectId, m.email, e.target.value as ProjectRole),
                            )
                          }
                        >
                          {ROLES.map((r) => (
                            <option key={r.value} value={r.value}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        roleLabel(m.role)
                      )}
                    </td>
                    <td>
                      {/*
                        ACCESS IS SET PER ROW AND ONLY BY AN OWNER, and only on somebody else's row.

                        An owner is never scoped — the database refuses it, the schema refuses it, and
                        `addMemberByEmail` clears it on promotion — so there is nothing to edit on
                        your own row and a control there would be a control whose only outcome is a
                        refusal. Owners' rows say so rather than showing a disabled box.
                      */}
                      {isOwner &&
                        (m.role === 'owner' ? (
                          <span className="muted small" data-testid={`member-unscoped-${m.userId}`}>
                            Full access
                          </span>
                        ) : (
                          <button
                            data-testid={`member-access-${m.userId}`}
                            disabled={busy}
                            onClick={() => setEditing(editing === m.userId ? null : m.userId)}
                          >
                            {m.scope === null && !m.salaryHidden ? 'Full access' : 'Restricted'}
                          </button>
                        ))}
                    </td>
                    <td>
                      {/*
                        TWO WORDS FOR ONE ROUTE, because they are two different acts. An owner
                        WITHDRAWS somebody else's access; anybody LEAVES their own — a consultant
                        rolling off, which is the ordinary case and had no control at all. The server
                        holds one rule ("an owner may remove anybody, and anybody may remove
                        themselves"); this names each half in the words of the person doing it.

                        The last-owner refusal is the server's and is not mirrored here: a sole owner
                        pressing Leave gets a sentence telling them to appoint somebody first, which
                        is more use than a button that was never there.
                      */}
                      {m.userId === currentUserId ? (
                        <button
                          data-testid="member-leave"
                          disabled={busy}
                          title="Give up your own access to this project. Refused if you are the last owner."
                          onClick={() =>
                            void run(async () => {
                              await api.removeMember(projectId, m.userId);
                              onLeft();
                            })
                          }
                        >
                          Leave
                        </button>
                      ) : (
                        isOwner && (
                          <button
                            data-testid={`member-remove-${m.userId}`}
                            disabled={busy}
                            title="Withdraw access, and close their open tabs on this project"
                            onClick={() => void run(() => api.removeMember(projectId, m.userId))}
                          >
                            Remove
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
                {/*
                  The editor as its own ROW rather than a second modal, because it is about the row
                  above it and a dialog over a dialog loses that. It is also why only one is open at a
                  time: setting somebody's access is a decision, not a bulk edit.
                */}
                {members.map((m) =>
                  editing === m.userId ? (
                    <tr key={`${m.userId}-scope`} data-testid={`member-scope-row-${m.userId}`}>
                      <td colSpan={5}>
                        <ScopeEditor
                          people={people}
                          scope={m.scope}
                          salaryHidden={m.salaryHidden}
                          disabled={busy}
                          onSave={(next) =>
                            void run(async () => {
                              await api.addMember(projectId, m.email, m.role, next);
                              setEditing(null);
                            })
                          }
                        />
                      </td>
                    </tr>
                  ) : null,
                )}
              </tbody>
            </table>
          )}

          {isOwner && (
            <>
              <h5 className="asm-grid-h">Give somebody access</h5>
              <p className="muted asm-help" style={{ marginTop: 0 }}>
                By the email they sign in with. There is no invitation: the address has to belong to an
                account that already exists, and an unknown one is refused rather than created.
              </p>
              <div className="field">
                <label htmlFor="member-email">Email</label>
                {/*
                  Controlled, unlike every other input in this app — and deliberately. `LiveField` exists
                  for a box that MIRRORS server state and must resync when a colleague moves it; this one
                  mirrors nothing, it is a draft address on the way to a button, and committing it on blur
                  would race the click that reads it.
                */}
                <input
                  id="member-email"
                  data-testid="member-email"
                  type="email"
                  value={email}
                  placeholder="colleague@example.com"
                  disabled={busy}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') grant();
                  }}
                />
              </div>
              <div className="field">
                <label htmlFor="member-new-role">Role</label>
                <select
                  id="member-new-role"
                  data-testid="member-new-role"
                  value={role}
                  disabled={busy}
                  onChange={(e) => setRole(e.target.value as ProjectRole)}
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
                <div className="muted asm-help" style={{ marginTop: 3 }}>
                  {ROLES.find((r) => r.value === role)?.help}
                </div>
              </div>
              <button
                className="primary"
                data-testid="member-add"
                disabled={busy}
                onClick={grant}
              >
                Give access
              </button>
            </>
          )}
    </Modal>
  );
}
